import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API } from "../services/config";
import "./ReservarTurnoPage.css";

const STEPS = ["profesional", "practica", "horario", "datos", "exito"];

function formatSlotLabel(isoStart) {
  try {
    const d = new Date(isoStart);
    return d.toLocaleString("es-AR", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStart;
  }
}

function groupSlotsByDay(slots) {
  const map = new Map();
  for (const slot of slots) {
    const key = String(slot.start).slice(0, 10);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(slot);
  }
  return Array.from(map.entries());
}

export default function ReservarTurnoPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState("profesional");

  const [profesionales, setProfesionales] = useState([]);
  const [practicas, setPracticas] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [profesional, setProfesional] = useState(null);
  const [practica, setPractica] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    dni: "",
    telefono: "",
    mail: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const accent = config?.color_acento || "#0A1B3D";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/publico/config`);
        const data = await res.json();
        if (!cancelled) {
          if (!data.success) throw new Error(data.message || "No se pudo cargar la configuración");
          setConfig(data.data);
          if (!data.data.permite_autogestion) {
            setError("La reserva online no está habilitada en este momento.");
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Error de conexión");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!config?.permite_autogestion) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/publico/profesionales`);
        const data = await res.json();
        if (!cancelled && data.success) setProfesionales(data.data || []);
      } catch {
        if (!cancelled) setError("No se pudieron cargar los profesionales.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config]);

  const loadPracticas = useCallback(async (profId) => {
    const res = await fetch(`${API}/publico/profesionales/${profId}/practicas`);
    const data = await res.json();
    if (!data.success) throw new Error(data.message || "No se pudieron cargar los servicios");
    setPracticas(data.data || []);
  }, []);

  const loadSlots = useCallback(async (profId, practicaId) => {
    setLoadingSlots(true);
    try {
      const desde = new Date();
      const hasta = new Date();
      hasta.setDate(hasta.getDate() + 21);
      const qs = new URLSearchParams({
        profesional_id: String(profId),
        practica_id: String(practicaId),
        desde: desde.toISOString().slice(0, 10),
        hasta: hasta.toISOString().slice(0, 10),
      });
      const res = await fetch(`${API}/publico/disponibilidad?${qs}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "No hay horarios");
      setSlots(data.data?.slots || []);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  const selectProfesional = async (p) => {
    setError("");
    setProfesional(p);
    setPractica(null);
    setSlot(null);
    setPracticas([]);
    setSlots([]);
    try {
      await loadPracticas(p.id);
      setStep("practica");
    } catch (e) {
      setError(e.message || "Error al cargar servicios");
    }
  };

  const selectPractica = async (pr) => {
    setError("");
    setPractica(pr);
    setSlot(null);
    try {
      await loadSlots(profesional.id, pr.id);
      setStep("horario");
    } catch (e) {
      setError(e.message || "Error al cargar horarios");
    }
  };

  const selectSlot = (s) => {
    setSlot(s);
    setStep("datos");
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit = useMemo(() => {
    return (
      form.nombre.trim() &&
      form.apellido.trim() &&
      form.dni.trim() &&
      form.telefono.trim() &&
      profesional &&
      practica &&
      slot &&
      !submitting
    );
  }, [form, profesional, practica, slot, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/publico/reservar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          profesional_id: profesional.id,
          practica_id: practica.id,
          start: slot.start,
          end: slot.end,
          paciente: {
            nombre: form.nombre.trim(),
            apellido: form.apellido.trim(),
            dni: form.dni.trim(),
            telefono: form.telefono.trim(),
            mail: form.mail.trim() || null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "No se pudo reservar el turno");
      }
      setResult(data);
      setStep("exito");
    } catch (err) {
      setError(err.message || "Error al reservar");
    } finally {
      setSubmitting(false);
    }
  };

  const resetFlow = () => {
    setStep("profesional");
    setProfesional(null);
    setPractica(null);
    setSlot(null);
    setResult(null);
    setForm({ nombre: "", apellido: "", dni: "", telefono: "", mail: "" });
    setError("");
  };

  const stepIndex = STEPS.indexOf(step);
  const groupedSlots = useMemo(() => groupSlotsByDay(slots), [slots]);

  if (loading) {
    return (
      <div className="reservar-page">
        <div className="reservar-card">
          <p className="reservar-muted">Cargando…</p>
        </div>
      </div>
    );
  }

  if (!config?.permite_autogestion) {
    return (
      <div className="reservar-page">
        <div className="reservar-card">
          <h1 className="reservar-title">Reserva online</h1>
          <p className="reservar-error">{error || "La reserva online no está disponible."}</p>
          <a className="reservar-link" href="/paciente/login">
            Ir al portal del paciente
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="reservar-page" style={{ "--reservar-accent": accent }}>
      <div className="reservar-card">
        <header className="reservar-header">
          {config.logo_url ? (
            <img src={config.logo_url} alt="" className="reservar-logo-img" />
          ) : (
            <div className="reservar-logo-fallback">
              {(config.nombre_clinica || "C").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <p className="reservar-clinic">{config.nombre_clinica}</p>
            <h1 className="reservar-title">{config.titulo}</h1>
            <p className="reservar-subtitle">{config.subtitulo}</p>
          </div>
        </header>

        {step !== "exito" && (
          <nav className="reservar-steps" aria-label="Progreso">
            {["Profesional", "Servicio", "Horario", "Datos"].map((label, idx) => (
              <span
                key={label}
                className={`reservar-step ${idx <= Math.min(stepIndex, 3) ? "is-active" : ""}`}
              >
                {label}
              </span>
            ))}
          </nav>
        )}

        {error && <div className="reservar-error" role="alert">{error}</div>}

        {step === "profesional" && (
          <section className="reservar-section">
            <h2 className="reservar-section-title">Elegí un profesional</h2>
            {profesionales.length === 0 ? (
              <p className="reservar-muted">No hay profesionales con horarios cargados.</p>
            ) : (
              <div className="reservar-list">
                {profesionales.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="reservar-choice"
                    onClick={() => selectProfesional(p)}
                  >
                    <span
                      className="reservar-choice-dot"
                      style={{ background: p.color || accent }}
                    />
                    <span>{p.nombre_completo || `${p.nombre} ${p.apellido}`}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {step === "practica" && (
          <section className="reservar-section">
            <button type="button" className="reservar-back" onClick={() => setStep("profesional")}>
              Volver
            </button>
            <h2 className="reservar-section-title">Elegí el servicio</h2>
            <p className="reservar-muted">
              Con {profesional?.nombre_completo || `${profesional?.nombre} ${profesional?.apellido}`}
            </p>
            <div className="reservar-list">
              {practicas.map((pr) => (
                <button
                  key={pr.id}
                  type="button"
                  className="reservar-choice reservar-choice--stack"
                  onClick={() => selectPractica(pr)}
                >
                  <strong>{pr.nombre}</strong>
                  <span className="reservar-muted">
                    {pr.duracion_minutos} min
                    {pr.precio != null ? ` · $${Number(pr.precio).toLocaleString("es-AR")}` : ""}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {step === "horario" && (
          <section className="reservar-section">
            <button type="button" className="reservar-back" onClick={() => setStep("practica")}>
              Volver
            </button>
            <h2 className="reservar-section-title">Elegí fecha y hora</h2>
            <p className="reservar-muted">{practica?.nombre}</p>
            {loadingSlots ? (
              <p className="reservar-muted">Buscando horarios…</p>
            ) : groupedSlots.length === 0 ? (
              <p className="reservar-muted">No hay turnos disponibles en las próximas semanas.</p>
            ) : (
              <div className="reservar-days">
                {groupedSlots.map(([day, daySlots]) => (
                  <div key={day} className="reservar-day">
                    <h3 className="reservar-day-title">
                      {new Date(`${day}T12:00:00`).toLocaleDateString("es-AR", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    </h3>
                    <div className="reservar-slot-grid">
                      {daySlots.map((s) => (
                        <button
                          key={s.start}
                          type="button"
                          className="reservar-slot"
                          onClick={() => selectSlot(s)}
                        >
                          {new Date(s.start).toLocaleTimeString("es-AR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {step === "datos" && (
          <section className="reservar-section">
            <button type="button" className="reservar-back" onClick={() => setStep("horario")}>
              Volver
            </button>
            <h2 className="reservar-section-title">Tus datos</h2>
            <div className="reservar-summary">
              <div>
                <span className="reservar-muted">Profesional</span>
                <strong>
                  {profesional?.nombre_completo || `${profesional?.nombre} ${profesional?.apellido}`}
                </strong>
              </div>
              <div>
                <span className="reservar-muted">Servicio</span>
                <strong>{practica?.nombre}</strong>
              </div>
              <div>
                <span className="reservar-muted">Horario</span>
                <strong>{formatSlotLabel(slot?.start)}</strong>
              </div>
            </div>

            <form className="reservar-form" onSubmit={handleSubmit}>
              <label>
                Nombre
                <input name="nombre" value={form.nombre} onChange={onChange} required autoComplete="given-name" />
              </label>
              <label>
                Apellido
                <input name="apellido" value={form.apellido} onChange={onChange} required autoComplete="family-name" />
              </label>
              <label>
                DNI
                <input name="dni" value={form.dni} onChange={onChange} required inputMode="numeric" />
              </label>
              <label>
                Teléfono / WhatsApp
                <input
                  name="telefono"
                  value={form.telefono}
                  onChange={onChange}
                  required
                  inputMode="tel"
                  placeholder="351..."
                />
              </label>
              <label>
                Email (opcional)
                <input name="mail" type="email" value={form.mail} onChange={onChange} autoComplete="email" />
              </label>
              <button type="submit" className="reservar-submit" disabled={!canSubmit}>
                {submitting ? "Reservando…" : "Confirmar turno"}
              </button>
            </form>
          </section>
        )}

        {step === "exito" && result && (
          <section className="reservar-section reservar-success">
            <div className="reservar-success-icon" aria-hidden>
              ✓
            </div>
            <h2 className="reservar-section-title">Turno reservado</h2>
            <p className="reservar-muted">
              {result.data?.practica} con {result.data?.profesional}
              <br />
              {formatSlotLabel(result.data?.start)}
            </p>

            {result.wa_bridge_required && result.wa_me_url ? (
              <div className="reservar-bridge">
                <p>
                  Para recibir recordatorios por WhatsApp, tocá el botón y enviá el mensaje al consultorio.
                  Es un paso único y necesario.
                </p>
                <a
                  className="reservar-wa-btn"
                  href={result.wa_me_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Activar recordatorios por WhatsApp
                </a>
              </div>
            ) : (
              <p className="reservar-ok-wa">
                Te vamos a enviar la confirmación por WhatsApp al número indicado.
              </p>
            )}

            <button type="button" className="reservar-secondary" onClick={resetFlow}>
              Reservar otro turno
            </button>
            <a className="reservar-link" href="/paciente/login">
              Ya tengo cuenta: ingresar al portal
            </a>
          </section>
        )}
      </div>
    </div>
  );
}
